/**
 * Module dependencies.
 */

import { Loading, Radio } from 'src/components/core';
import { Market, advisorMarketTypes } from 'src/types/markets';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { advisorFiltersKeys } from 'src/core/advisor-config';
import { orderedAdvisorMarketTypes } from 'src/core/advisor-config/ordered-types';
import { stepsKeys } from 'src/core/advisor-config/steps-options';
import { units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import BaseStep from './base-step';
import camelCase from 'lodash/camelCase';
import compact from 'lodash/compact';
import concat from 'lodash/concat';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import useMarkets from 'src/api/markets/use-markets';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  markets: Market[];
};

/**
 * `pageKey`.
 */

const pageKey = stepsKeys.typeGas;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  position: relative;
`;

/**
 * `OptionsWrapper` styled component.
 */

const OptionsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
  margin-bottom: ${units(6)};
`;

/**
 * `StyledRadio` styled component.
 */

const StyledRadio = styled(Radio)`
  font-weight: 700;
`;

/**
 * `MarketsStep` component.
 */

const MarketsStep = ({ className, markets }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { getCheckedValue, onGoToNext, onGoToPrevious, onSetFilter, refetch } =
    useAdvisor();

  const { data, isLoading, isSuccess } = useMarkets({ initialData: markets });

  const marketTypes = useMemo(() => {
    const marketTypes = compact(
      map(Object.values(orderedAdvisorMarketTypes), type => {
        if (find(data, ['advisorMarketType', type])) {
          return type;
        }
      })
    );

    if (!includes(marketTypes, advisorMarketTypes.helium)) {
      return concat(marketTypes, advisorMarketTypes.helium);
    }
  }, [data]);

  const checkedValue = getCheckedValue(pageKey);
  const [value, setValue] = useState<string | null>(checkedValue);
  const handleContinue = useCallback(() => {
    onSetFilter(advisorFiltersKeys.market, JSON.stringify(value));
    refetch();
    onGoToNext(value, pageKey, 0);
  }, [onGoToNext, onSetFilter, refetch, value]);

  const handleChange = useCallback(
    ({ target }) => {
      setValue(target.value);
      onSetFilter(advisorFiltersKeys.market, JSON.stringify(target.value));
      refetch();
      onGoToNext(target.value, pageKey, 0);
    },
    [onGoToNext, onSetFilter, refetch]
  );

  return (
    <Wrapper className={className}>
      {isLoading && <Loading active={isLoading} />}

      {isSuccess && (
        <BaseStep
          canContinue={!isEmpty(checkedValue)}
          description={translate('advisor:steps.typeGas.description')}
          filterKey={advisorFiltersKeys.market}
          isFirstStep
          onContinue={handleContinue}
          onGoBack={() => onGoToPrevious(0)}
          pageIndex={0}
          pageKey={pageKey}
          title={translate('advisor:steps.typeGas.title')}
        >
          <OptionsWrapper>
            {map(marketTypes, (marketType, index) => (
              <StyledRadio
                checked={checkedValue && checkedValue === marketType}
                key={index}
                label={translate(`advisor:labels.${camelCase(marketType)}`)}
                name={'advisor-market'}
                onChange={handleChange}
                value={marketType}
              />
            ))}
          </OptionsWrapper>
        </BaseStep>
      )}
    </Wrapper>
  );
};

/**
 * Export `MarketsStep` component.
 */

export default MarketsStep;
